import { useState } from "react";
import { Alert, Button, Card, Table } from "../../ds";
import type { DeviceDetail, NetworkStatus } from "../../lib/api";
import { useConfirmProbe } from "../../lib/hooks/useConfirmProbe";
import { useSection } from "../../lib/hooks/useSection";
import { EntryModal, type FieldSpec } from "./EntryModal";
import { ProbeBanner } from "./ProbeBanner";
import { mwanLists, mwanSpecs, type MwanEntry, type MwanKey, type MwanSpec } from "./mwanSpecs";

// Failover zwischen mehreren Uplinks. Optional: das Paket ist auf den meisten
// Boxen nicht installiert — dann sagt die Karte das, statt ausgegraute Knöpfe zu
// zeigen, hinter denen nichts steht.
//
// Geschrieben wird auf Probe wie bei der Firewall: mwan3 verwaltet die
// Leitungen, über die der Mgmt-Kanal selbst läuft.

/** Muss zu `MWAN_REVERT_IN` in server/core/writes.py passen. */
const REVERT_IN = 120;

type Lists = Record<MwanKey, MwanEntry[]>;

/** Feldwerte einer Zeile in Wire-Params — leere Felder bleiben weg, dann lässt
 *  der Agent die uci-Option unangetastet. */
function rowParams(entry: MwanEntry, fields: FieldSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.key === "id") continue;
    const value = (entry as unknown as Record<string, unknown>)[field.key];
    if (value === null || value === undefined || value === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[field.wire ?? field.key] = value;
  }
  return out;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Entwurf → `mwan_set`-Params. Neue Zeilen nennen `name`, bekannte `id`,
 * verschwundene landen in der Lösch-Liste — mwan3 kennt keinen Desired-State,
 * eine vergessene Zeile wäre sonst ein Uplink, den niemand mehr überwacht.
 */
function toParams(specs: MwanSpec<MwanEntry>[], draft: Lists, base: Lists) {
  const params: Record<string, unknown> = {};
  for (const spec of specs) {
    const known = new Map(base[spec.key].map((e) => [e.id, e]));
    const rows: Record<string, unknown>[] = [];
    for (const entry of draft[spec.key]) {
      const fields = rowParams(entry, spec.fields);
      const before = known.get(entry.id);
      if (!before) rows.push({ name: entry.id, ...fields });
      else if (!same(fields, rowParams(before, spec.fields)))
        rows.push({ id: entry.id, ...fields });
    }
    const kept = new Set(draft[spec.key].map((e) => e.id));
    const gone = base[spec.key].filter((e) => !kept.has(e.id)).map((e) => e.id);
    if (rows.length) params[spec.key] = rows;
    if (gone.length) params[spec.removeKey] = gone;
  }
  return params;
}

function LevelCard({
  spec,
  rows,
  onAdd,
}: {
  spec: MwanSpec<MwanEntry>;
  rows: MwanEntry[];
  onAdd: () => void;
}) {
  return (
    <Card
      title={spec.title}
      subtitle={spec.description}
      bodyPadding={false}
      actions={
        <Button size="sm" variant="outline" iconName="plus" onClick={onAdd}>
          {spec.singular} anlegen
        </Button>
      }
    >
      <Table
        columns={spec.columns}
        rows={rows}
        rowKey={(entry, i) => entry.id || `neu-${i}`}
        emptyMessage={spec.empty}
      />
    </Card>
  );
}

export function MwanCard({ device }: { device: DeviceDetail }) {
  const section = useSection<NetworkStatus>(device, "network");
  const probe = useConfirmProbe(device.id, section, {
    setAction: "mwan_set",
    confirmAction: "mwan_confirm",
    revertIn: REVERT_IN,
  });
  const [draft, setDraft] = useState<Lists | null>(null);
  const [editing, setEditing] = useState<{
    key: MwanKey;
    index: number;
    entry: MwanEntry;
  } | null>(null);

  const mwan = section.data?.mwan;
  const base = mwanLists(mwan);
  const current = draft ?? base;
  const dirty = draft !== null;

  // Erst nachdem der Stand einmal da war, lässt sich „nicht installiert" von
  // „noch nicht abgefragt" unterscheiden.
  if (section.data === null) return null;

  if (!mwan) {
    return (
      <Card title="Failover (mwan3)">
        <Alert variant="info" title="mwan3 ist auf diesem Router nicht installiert">
          Kein verwalteter Failover verfügbar. Uplink mit kleinster Routen-Metrik gewinnt.
        </Alert>
      </Card>
    );
  }

  const specs = mwanSpecs(
    (key, entry, index) => setEditing({ key, index, entry: { ...entry } }),
    (key, index) =>
      setDraft((d) => {
        const from = d ?? base;
        return { ...from, [key]: from[key].filter((_, i) => i !== index) };
      }),
  );

  function saveEntry() {
    if (!editing) return;
    const { key, index, entry } = editing;
    setDraft((d) => {
      const from = d ?? base;
      const rows = [...from[key]];
      if (index < 0) rows.push(entry);
      else rows[index] = entry;
      return { ...from, [key]: rows };
    });
    setEditing(null);
  }

  async function apply() {
    const params = toParams(specs, current, base);
    if (!Object.keys(params).length) {
      setDraft(null);
      return;
    }
    const optimistic = section.data
      ? { ...section.data, mwan: { ...mwan, ...current } }
      : undefined;
    if (await probe.apply(params, optimistic)) setDraft(null);
  }

  const fields = editing ? (specs.find((s) => s.key === editing.key)?.fields ?? null) : null;

  return (
    <>
      <Card
        title="Failover (mwan3)"
        subtitle="Interfaces, Member, Policies und Regeln — Schreiben auf Probe"
        actions={
          <>
            {dirty && (
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Verwerfen
              </Button>
            )}
            <Button
              size="sm"
              variant="primary"
              iconName="check"
              disabled={!dirty || !device.online || probe.phase === "probe"}
              loading={probe.applying}
              onClick={() => void apply()}
            >
              Anwenden
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          {probe.note && (
            <Alert variant={probe.note.variant} dismissible onDismiss={() => probe.setNote(null)}>
              {probe.note.text}
            </Alert>
          )}
          <ProbeBanner
            phase={probe.phase}
            secondsLeft={probe.secondsLeft}
            keeping={probe.keeping}
            hint="mwan3 läuft mit der neuen Konfiguration — Routing und Erreichbarkeit prüfen."
            onKeep={() => void probe.keep()}
            onDismiss={probe.dismiss}
          />
          {dirty && (
            <Alert variant="info">
              Entwurf nicht angewendet — auf dem Router läuft der alte Stand.
            </Alert>
          )}
          {specs.map((spec) => (
            <LevelCard
              key={spec.key}
              spec={spec}
              rows={current[spec.key]}
              onAdd={() => setEditing({ key: spec.key, index: -1, entry: { ...spec.blank } })}
            />
          ))}
          <p className="text-xs text-content-3">
            Regel → Policy → Member → Interface: der Server lehnt ab, was diese Kette bricht. Neuer
            Uplink samt mwan3-Eintrag: oben „Uplink hinzufügen“.
          </p>
        </div>
      </Card>

      <EntryModal
        fields={fields}
        singular={editing ? (specs.find((s) => s.key === editing.key)?.singular ?? "") : ""}
        entry={editing?.entry ?? null}
        isNew={(editing?.index ?? -1) < 0}
        subtitle="Die Änderung geht erst mit „Anwenden“ auf Probe zum Router."
        onChange={(entry) => setEditing((e) => (e ? { ...e, entry } : e))}
        onSave={saveEntry}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
